import { Link, useLocation, useNavigate } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { Calculator, Users, BookOpen, LayoutGrid, LogOut, ListChecks, Banknote, Printer } from "lucide-react";

const NAV_GROUPS = [
  { header: null, items: [
    { to: "/app", label: "Índice", icon: LayoutGrid, exact: true },
    { to: "/app/legajos", label: "Legajos", icon: Users },
  ]},
  { header: "CALCULADORAS", items: [
    { to: "/app/calculadora", label: "Aguinaldo", icon: Calculator },
    { to: "/app/sueldos", label: "Recibo de Sueldos", icon: Banknote, exact: true },
  ]},
  { header: "DATOS GUARDADOS", items: [
    { to: "/app/lista", label: "Lista", icon: ListChecks },
  ]},
  { header: "IMPRESIÓN", items: [
    { to: "/app/recibos", label: "Recibos", icon: Printer, exact: true },
  ]},
  { header: null, items: [
    { to: "/app/legislacion", label: "Legislación", icon: BookOpen },
  ]},
];

export function BrandLogo({ size = "md" }) {
  const { branding } = useBranding();
  const logo = branding.logoLight;
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      {logo ? (
        <img src={logo} alt="logo" className="h-8 w-auto max-w-[120px] object-contain" />
      ) : (
        <div className="h-8 w-8 brand-bg flex items-center justify-center text-white font-head font-bold">
          {(branding.appName || "H")[0]}
        </div>
      )}
      <span className={`font-head font-bold tracking-tight ${size === "lg" ? "text-2xl" : "text-lg"} brand-text`}>
        {branding.appName}
      </span>
    </div>
  );
}

export default function AppLayout({ children, title, subtitle, actions }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout, user } = useAuth();

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className="w-60 bg-white border-r border-border hidden md:flex flex-col">
        <div className="p-6 border-b border-border">
          <Link to="/app"><BrandLogo /></Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi}>
              {group.header && (
                <div className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-widest text-slate-400" data-testid={`nav-group-${group.header.toLowerCase().replace(/\s/g, "-")}`}>
                  {group.header}
                </div>
              )}
              {group.items.map((item) => {
                const active = item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                      active ? "brand-bg text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <Icon size={17} strokeWidth={1.6} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        {user?.role === "admin" && (
          <Link to="/admin" className="mx-3 mb-2 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-100">
            Panel Admin
          </Link>
        )}
        <button
          onClick={() => { logout(); nav("/"); }}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-6 py-4 text-sm text-slate-500 hover:text-destructive border-t border-border"
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-border px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-head text-xl sm:text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
        <main className="flex-1 p-6 enter-up">{children}</main>
      </div>
    </div>
  );
}
